import { ArrowRight, Eye, Target, Droplets, Package } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AppStoreBadge } from '../components/StoreButtons';
import Seo from '../components/Seo';

const FlyGuide = () => {
  return (
    <div className="min-h-screen bg-[#0F1419]">
      <Seo path="/fly-guide" title="Fly Fishing Guide | FlyFinder" description="A complete fly fishing guide: understanding aquatic insects, the 4 essential fly types, reading the water, and building your essential fly box for North American waters." />
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-cyan-600 to-cyan-800 text-white">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://customer-assets.emergentagent.com/job_fishing-companion-4/artifacts/r9xhllmy_matthew-mcbrayer-jh8tvY_wY_U-unsplash.jpg"
            alt="Fly fishing in beautiful waters"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            Complete Fly Guide
          </h1>
          <p className="text-lg md:text-xl text-cyan-100 max-w-2xl mx-auto">
            Your comprehensive resource for fly fishing success across North America
          </p>
        </div>
      </section>

      {/* Complete Fly Section Guide */}
      <section className="py-24 bg-[#151b21]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {/* Understanding Canadian Aquatic Insects */}
            <Link to="/guide/aquatic-insects" className="group bg-[#1E2328] rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-2 border-transparent hover:border-cyan-500">
              <div className="w-16 h-16 bg-[#137dc5]/15 rounded-xl flex items-center justify-center mb-6 group-hover:bg-cyan-200 transition-colors">
                <Eye className="w-8 h-8 text-[#3aa0e0]" />
              </div>
              <h4 className="text-2xl font-bold text-white mb-3 group-hover:text-[#3aa0e0] transition-colors">
                Understanding Canadian Aquatic Insects
              </h4>
              <p className="text-slate-400 leading-relaxed">
                Learn about mayflies, caddis, stoneflies, midges, and terrestrials. Understand what fish are eating throughout the season across North American waters.
              </p>
              <div className="mt-6 inline-flex items-center text-[#3aa0e0] font-semibold group-hover:gap-3 gap-2 transition-all">
                Explore Guide
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            {/* The 4 Essential Fly Types */}
            <Link to="/guide/fly-types" className="group bg-[#1E2328] rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-2 border-transparent hover:border-cyan-500">
              <div className="w-16 h-16 bg-[#137dc5]/15 rounded-xl flex items-center justify-center mb-6 group-hover:bg-cyan-200 transition-colors">
                <Target className="w-8 h-8 text-[#3aa0e0]" />
              </div>
              <h4 className="text-2xl font-bold text-white mb-3 group-hover:text-[#3aa0e0] transition-colors">
                The 4 Essential Fly Types
              </h4>
              <p className="text-slate-400 leading-relaxed">
                Master dry flies, nymphs, emergers, and streamers. Learn when and how to use each type for maximum success.
              </p>
              <div className="mt-6 inline-flex items-center text-[#3aa0e0] font-semibold group-hover:gap-3 gap-2 transition-all">
                Explore Guide
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            {/* Reading the Water */}
            <Link to="/guide/reading-water" className="group bg-[#1E2328] rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-2 border-transparent hover:border-cyan-500">
              <div className="w-16 h-16 bg-[#137dc5]/15 rounded-xl flex items-center justify-center mb-6 group-hover:bg-cyan-200 transition-colors">
                <Droplets className="w-8 h-8 text-[#3aa0e0]" />
              </div>
              <h4 className="text-2xl font-bold text-white mb-3 group-hover:text-[#3aa0e0] transition-colors">
                Reading the Water
              </h4>
              <p className="text-slate-400 leading-relaxed">
                Develop the skills to read water conditions, identify feeding patterns, and determine what flies to use.
              </p>
              <div className="mt-6 inline-flex items-center text-[#3aa0e0] font-semibold group-hover:gap-3 gap-2 transition-all">
                Explore Guide
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            {/* Essential Fly Box */}
            <Link to="/guide/fly-box" className="group bg-[#1E2328] rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-2 border-transparent hover:border-cyan-500">
              <div className="w-16 h-16 bg-[#137dc5]/15 rounded-xl flex items-center justify-center mb-6 group-hover:bg-cyan-200 transition-colors">
                <Package className="w-8 h-8 text-[#3aa0e0]" />
              </div>
              <h4 className="text-2xl font-bold text-white mb-3 group-hover:text-[#3aa0e0] transition-colors">
                Essential Fly Box
              </h4>
              <p className="text-slate-400 leading-relaxed">
                Build your perfect fly box with must-have patterns for successful fly fishing.
              </p>
              <div className="mt-6 inline-flex items-center text-[#3aa0e0] font-semibold group-hover:gap-3 gap-2 transition-all">
                Explore Guide
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-[#0b0f13] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Start Your Journey to Better Fly Selection</h2>
          <p className="text-xl text-slate-300 mb-12">
            Join thousands of anglers who are already using FlyFinder to improve their success on the water.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <div className="flex justify-center">
            <AppStoreBadge className="h-14" />
          </div>
          </div>
          <p className="text-slate-400 mt-6">Now available on the Apple App Store</p>
        </div>
      </section>
    </div>
  );
};

export default FlyGuide;
